import { NgTemplateOutlet } from '@angular/common';
import { Component, computed, inject, input } from '@angular/core';
import { Store } from '@ngrx/store';
import { TranslateModule } from '@ngx-translate/core';
import * as fromSeriousSystem from '@serious-system';
import * as fromGenerated from '../../../_generated';
import { AnalyticsService } from '../../../analytics/analytics.service';
import * as fromLayout from '../../../layout';
import * as fromShared from '../../shared';
import { chatsFeature, ChatsState } from '../../store';

@Component({
  selector: 'squadbox-navigation-sources-list-smart',
  imports: [
    NgTemplateOutlet,
    TranslateModule,
    fromSeriousSystem.UseUIBasicIconDirective,
    fromSeriousSystem.UseNavigationIconDirective,
    fromSeriousSystem.UseFileTypeIconDirective,
    fromSeriousSystem.SpinnerDirective,
  ],
  template: `
    <ng-template #sourceRow let-source>
      @let isSelected = source.uuid === selectedDocumentId();
      <div
        role="button"
        [attr.tabindex]="isSourceDisabled(source.status) ? -1 : 0"
        class="navigation-item"
        [class.disabled]="isSourceDisabled(source.status)"
        [class.selected]="isSelected"
        (click)="
          handleSourceClicked(
            source.uuid,
            source.chatMessageId,
            source.status,
            source.isImage,
            source.filename
          )
        "
        (keydown.enter)="
          handleSourceClicked(
            source.uuid,
            source.chatMessageId,
            source.status,
            source.isImage,
            source.filename
          )
        "
        (keydown.space)="
          handleSourceClicked(
            source.uuid,
            source.chatMessageId,
            source.status,
            source.isImage,
            source.filename
          );
          $event.preventDefault()
        "
      >
        <div
          class="flex flex-row items-center gap-2 w-full"
          [class.selected]="isSelected"
        >
          @if (source.status === 'processing') {
            <span class="shrink-0" sdSpinner></span>
          } @else if (source.status === 'failed') {
            <svg
              sdUseUIBasicIcon="warning-filled"
              class="size-5 shrink-0 text-error-500"
            ></svg>
          } @else {
            <svg
              [sdUseFileTypeIcon]="source.fileType"
              class="size-5 shrink-0"
            ></svg>
          }
          <p class="line-clamp-1 break-all">
            {{ sourceRowTitle(source) }}
          </p>
        </div>
      </div>
    </ng-template>

    <ng-template #emptyState let-emptyMessageKey>
      <div class="navigation-empty-state">
        <svg
          sdUseNavigationIcon="document"
          class="size-5 shrink-0 text-primary-300"
        ></svg>
        <p class="typo-caption">{{ emptyMessageKey | translate }}</p>
      </div>
    </ng-template>

    <div class="flex flex-1 flex-col gap-1">
      @let _sources = sources();
      @if (isComplianceTrait()) {
        <div class="flex flex-col gap-4">
          <!-- Reference Documents -->
          <div class="flex flex-col gap-1">
            <div class="navigation-label">
              {{ 'CHAT.ASSISTANT_PROFILE.REFERENCE_DOCUMENTS' | translate }}
            </div>
            @if (_sources.forComplianceTrait.references.length === 0) {
              <ng-container
                [ngTemplateOutlet]="emptyState"
                [ngTemplateOutletContext]="{
                  $implicit: 'CHAT.ASSISTANT_PROFILE.NO_REFERENCE_DOCUMENTS',
                }"
              />
            } @else {
              @for (
                source of _sources.forComplianceTrait.references;
                track source.uuid
              ) {
                <ng-container
                  [ngTemplateOutlet]="sourceRow"
                  [ngTemplateOutletContext]="{ $implicit: source }"
                />
              }
            }
          </div>
          <!-- Documents to analyze -->
          <div class="flex flex-col gap-1">
            <div class="navigation-label">
              {{ 'CHAT.ASSISTANT_PROFILE.DOCUMENTS_TO_ANALYZE' | translate }}
            </div>
            @if (_sources.forComplianceTrait.regulars.length === 0) {
              <ng-container
                [ngTemplateOutlet]="emptyState"
                [ngTemplateOutletContext]="{
                  $implicit: 'CHAT.ASSISTANT_PROFILE.NO_DOCUMENTS_TO_ANALYZE',
                }"
              />
            } @else {
              @for (
                source of _sources.forComplianceTrait.regulars;
                track source.uuid
              ) {
                <ng-container
                  [ngTemplateOutlet]="sourceRow"
                  [ngTemplateOutletContext]="{ $implicit: source }"
                />
              }
            }
          </div>
        </div>
      } @else {
        <div class="navigation-label">
          {{ headerLabel() | translate }}
        </div>
        @if (_sources.default.length === 0) {
          <ng-container
            [ngTemplateOutlet]="emptyState"
            [ngTemplateOutletContext]="{ $implicit: emptyStateMessage() }"
          />
        } @else {
          @for (source of _sources.default; track source.uuid) {
            <ng-container
              [ngTemplateOutlet]="sourceRow"
              [ngTemplateOutletContext]="{ $implicit: source }"
            />
          }
        }
      }
    </div>
  `,
})
export class NavigationSourcesListSmartComponent {
  private readonly layoutStore = inject<Store<fromLayout.LayoutState>>(Store);
  private readonly chatsStore = inject<Store<ChatsState>>(Store);
  private readonly analyticsService = inject(AnalyticsService);

  public readonly selectedAssistant =
    input.required<
      Pick<
        fromGenerated.AssistantView,
        'uuid' | 'name' | 'logo' | 'description' | 'documents' | 'trait'
      >
    >();

  public readonly isMobile = this.layoutStore.selectSignal(
    fromLayout.layoutFeature.selectIsMobile,
  );
  public readonly selectedDocumentId = this.chatsStore.selectSignal(
    chatsFeature.selectSelectedDocumentId,
  );
  public readonly selectedChat = this.chatsStore.selectSignal(
    chatsFeature.selectSelectedChat,
  );

  private readonly selectedChatDocuments = this.chatsStore.selectSignal(
    fromShared.selectSelectedChatDocuments,
  );

  public readonly isComplianceTrait = computed(
    () => this.selectedAssistant().trait === 'compliance',
  );

  public readonly sources = computed(
    () => this.selectedChatDocuments().toSources,
  );

  public sourceRowTitle(source: {
    filename: string;
    fileType: fromSeriousSystem.FileTypesIconsType;
  }): string {
    return fromSeriousSystem.fileDisplayNameForTypeIcon(
      source.filename,
      source.fileType,
    );
  }

  public isSourceDisabled(
    status: fromGenerated.DocumentView.StatusEnum,
  ): boolean {
    return (
      this.selectedAssistant().trait === 'creative' ||
      status === 'processing' ||
      status === 'failed'
    );
  }

  public handleSourceClicked(
    sourceUuid: string,
    chatMessageId: number | null,
    status: fromGenerated.DocumentView.StatusEnum,
    isImage: boolean,
    filename: string,
  ) {
    if (this.isSourceDisabled(status)) {
      return;
    }

    if (isImage) {
      /**
       * Route image-type sources to the overlay viewer instead
       * of the PDF-only document viewer. A Sources entry is a sent image, so it
       * maps to `openImageFromChatMessage` (resolved via the presigned-URL
       * endpoint). This also keeps images off the citation path —
       * `requestDocumentCitations` is only dispatched by the document viewer,
       * which images now bypass.
       */
      this.chatsStore.dispatch(
        fromShared.ImageOverlayActions.openImageFromChatMessage({
          documentUuid: sourceUuid,
          filename,
        }),
      );
      // Track overlay open from a Sources-panel image entry.
      this.analyticsService
        .sendImageOverlayOpenedEvent({ source: 'sources' })
        .subscribe();
      return;
    }

    this.chatsStore.dispatch(
      fromShared.DocumentViewerActions.openDocumentFromAssistantProfile({
        documentUuid: sourceUuid,
        chatMessageId,
      }),
    );
  }

  protected readonly emptyStateMessage = computed(() => {
    const trait = this.selectedAssistant().trait;
    return trait === 'comparison' || trait === 'compliance'
      ? 'CHAT.ASSISTANT_PROFILE.NO_DOCUMENTS'
      : 'CHAT.ASSISTANT_PROFILE.NO_SOURCES';
  });

  protected readonly headerLabel = computed(() => {
    const trait = this.selectedAssistant().trait;
    return trait === 'comparison' || trait === 'compliance'
      ? 'CHAT.ASSISTANT_PROFILE.DOCUMENTS'
      : 'CHAT.ASSISTANT_PROFILE.SOURCES';
  });
}
